// client/src/coms/views/canvas_parts/party/PartySessionScreen.js
// One party game session inside the Games destination: the visitor gate, the lobby, the game stage and
// the results, plus a bar to go back to the Games catalogue without leaving the game.
import React, { useCallback, useEffect, useState } from 'react';
import './partyGames.css';
import PartyLobby from './PartyLobby';
import PartyStage from './PartyStage';
import PartyResults from './PartyResults';
import usePartySession, { describePartyError } from './usePartySession';
import { MODE_ICONS } from './partyMeta';

const STATUS_TEXT = { lobby: 'In the lobby', playing: 'Playing', finished: 'Game over' };

export function Toasts({ error, notice }) {
  if (!error && !notice) return null;
  return (
    <div className="party-toasts" aria-live="assertive">
      {error ? <div className="party-toast error" role="alert">{error}</div> : null}
      {notice ? <div className="party-toast" role="status">{notice}</div> : null}
    </div>
  );
}

function VisitorGate({ view, onJoin, onBack }) {
  const playing = view.status === 'playing';
  const canPlay = view.status !== 'playing' || view.settings.allowLateJoin;
  return (
    <div className="party-gate" style={{ '--party-accent': view.mode.accent }} data-testid="party-gate">
      <div className="party-lobby-icon" aria-hidden="true">{MODE_ICONS[view.mode.icon] || '🎨'}</div>
      <h2>{view.mode.name}</h2>
      <p>{view.members.filter((m) => m.role === 'player').length}/{view.maxPlayers} players · {playing ? 'game in progress' : 'waiting in the lobby'}</p>
      <div className="party-row">
        {canPlay ? <button type="button" className="party-btn big" onClick={() => onJoin(false)} data-testid="gate-join">Join game</button> : null}
        {view.settings.allowSpectators ? <button type="button" className="party-btn ghost" onClick={() => onJoin(true)}>Watch</button> : null}
        <button type="button" className="party-btn ghost" onClick={onBack}>Back</button>
      </div>
    </div>
  );
}

// `intent` is 'join' or 'spectate' when arriving from a Join/Watch button, 'restore' for a game remembered
// from earlier, '' when already a member. `onBack` returns to the catalogue and keeps the game running;
// `onExit(message)` is called once the player has left or the game is gone.
export default function PartySessionScreen({ sessionId, intent = '', friends = [], server = null, modes = [], onExit, onBack, onView }) {
  const game = usePartySession(sessionId);
  const { view, closed } = game;
  const [joinTried, setJoinTried] = useState(false);

  const join = useCallback(async (spectate) => {
    const result = await game.act('join', { spectate });
    if (result && !result.ok) game.setError(describePartyError(result.data && result.data.error));
  }, [game]);

  useEffect(() => {
    if (!view || joinTried || view.me.role !== 'visitor' || !intent || intent === 'restore') return;
    setJoinTried(true);
    join(intent === 'spectate');
  }, [intent, join, joinTried, view]);

  useEffect(() => {
    // A game remembered from an earlier visit may simply be over; that needs no message.
    if (closed) onExit(closed === 'left' || (intent === 'restore' && closed === 'session_not_found') ? '' : describePartyError(closed));
  }, [closed, intent, onExit]);

  useEffect(() => {
    if (view && typeof onView === 'function') onView(view);
  }, [onView, view]);

  const leave = useCallback(async () => {
    const result = await game.act('leave');
    if (!result || result.ok || result.status === 404) onExit('');
  }, [game, onExit]);

  if (!view) return <div className="party-empty">Loading game…</div>;
  let screen = null;
  if (view.me.role === 'visitor') screen = <VisitorGate view={view} onJoin={join} onBack={onBack || (() => onExit(''))} />;
  else if (view.status === 'lobby') screen = <PartyLobby game={game} view={view} friends={friends} server={server} modes={modes} onLeave={leave} />;
  else if (view.status === 'playing' && view.game) screen = <PartyStage game={game} view={view} onLeave={leave} />;
  else if (view.status === 'finished') screen = <PartyResults game={game} view={view} onLeave={leave} />;
  return (
    <div className="party-session-screen" data-testid="party-session" data-status={view.status}>
      {onBack ? (
        <div className="games-session-bar">
          <button type="button" className="party-btn small ghost" onClick={onBack} data-testid="games-back">← Games</button>
          <span className="games-session-title">{MODE_ICONS[view.mode.icon] || '🎨'} {view.mode.name}</span>
          <span className="party-muted">{STATUS_TEXT[view.status] || ''}</span>
        </div>
      ) : null}
      {screen}
      <Toasts error={game.error} notice={game.notice} />
    </div>
  );
}
